import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Alert, Button, Col, Divider, Row, Switch, Tooltip, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import CoProducerClass from 'classes/CoProducer';
import Spin from 'components/Spin';
import type ICoProducer from 'interfaces/ICoProducer';
import type { IrregularProduct } from 'scenes/Producer/Product/MyProducts/constants';
import RegistryBlockModal from 'v2/components/RegistryBlockModal';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import { useRegistryCheck } from 'v2/hooks/useRegistryCheck';
import productService from 'v2/services/product';

import { FormContent } from '../FormContent';
import PartnerCardPreview from './components/Card';
import CoProducerForm from './components/CoProducerForm';
import { SolidaritySquadAlert } from './components/SolidaritySquad/Alert';
import { SolidaritySquadForm } from './components/SolidaritySquad/Form';

const putIndexOnArray = (array: any[]) => {
  return array.map((a, index) => ({
    ...a,
    originalIndex: index
  }));
};

const MAX_COMISSION_TOTAL = 100;

function parseCoproducerCommissionValue(value: ICoProducer['comission_value']): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'string' ? Number(String(value).replace(',', '.')) : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function calculateNewCopsComission(newCops: Array<ICoProducer>): Array<ICoProducer> {
  const sumOthers = newCops
    .filter(c => (c.active || c.new) && !c.pending && !c.is_producer)
    .reduce((acc, c) => acc + parseCoproducerCommissionValue(c.comission_value), 0);

  const producerShare = Math.max(0, Number((MAX_COMISSION_TOTAL - sumOthers).toFixed(2)));

  return newCops.map(cop => {
    if (cop.is_producer && (cop.active || cop.new) && !cop.pending) {
      return { ...cop, comission_value: producerShare, edited: true };
    }

    return cop;
  });
}

interface CoProducerProps {
  coproducers?: Array<ICoProducer>;
  loading: boolean;
  onChange: (...args: any[]) => any;
  fade: boolean;
  onChangeRequiredFields?: (...args: any[]) => any;
  canAddCoproducer: boolean;
  coProducerErrorMessage: string;
  hasElevarAdded: boolean;
}

const DEFAULT_COMMISSION_VALUE = 5;
const PROJECT_ELEVAR_ID = 75879698;

function CoProducer(props: CoProducerProps) {
  const {
    coproducers,
    loading,
    onChange,
    onChangeRequiredFields,
    canAddCoproducer,
    coProducerErrorMessage,
    hasElevarAdded
  } = props;

  const { productId } = useParams<{ productId: string }>();
  const listNewRef = useRef<HTMLDivElement>(null);

  const [showActive, setShowActive] = useState(true);
  const [showPending, setShowPending] = useState(true);
  const [showNew, setShowNew] = useState(true);
  const [pendingActions, setPendingActions] = useState<IrregularProduct['pending_actions']>(null);
  const [showRegistryBlockModal, setShowRegistryBlockModal] = useState(false);

  const permissions = useGetPermissions();
  const { data: registryCheck, shouldBlock, dismissBlock } = useRegistryCheck();

  const add = useCallback(() => {
    const newcp = new CoProducerClass({ new: true, pending: false });
    onChange([...(coproducers || []), newcp]);
  }, [coproducers, onChange]);

  const scrollToListNew = useCallback(() => {
    setTimeout(() => {
      listNewRef.current?.scrollIntoView();
    }, 0);
  }, []);

  const removeCommissionFromProducer = useCallback(
    (coproducersList: ICoProducer[], commissionValue = DEFAULT_COMMISSION_VALUE) => {
      return coproducersList.map(cop => {
        if (cop.is_producer) {
          if (typeof cop.comission_value === 'string') {
            cop.comission_value = Number((cop.comission_value as string).replace(',', '.'));
          }

          cop.comission_value -= commissionValue;
          cop.edited = true;
        }

        return cop;
      });
    },
    []
  );

  const addElevar = useCallback(
    (commissionValue = DEFAULT_COMMISSION_VALUE) => {
      const newCoproducers: Array<ICoProducer> = coproducers || [];
      const isElevarAlreadyAdded = newCoproducers.some(cop => cop.customer_id === PROJECT_ELEVAR_ID);

      if (isElevarAlreadyAdded) {
        scrollToListNew();
        return;
      }

      const elevar = new CoProducerClass({
        new: true,
        pending: false,
        business_name: 'Instituto Elevar',
        email: 'contato@projetoelevar.com.br',
        comission_type: 'P',
        comission_value: commissionValue,
        customer_id: PROJECT_ELEVAR_ID
      });

      const updatedCoproducers = removeCommissionFromProducer(newCoproducers, commissionValue);

      onChange([...updatedCoproducers, elevar]);
      scrollToListNew();
    },
    [coproducers, onChange, removeCommissionFromProducer, scrollToListNew]
  );

  const remove = useCallback(
    (k: any) => {
      const newCoproducers: Array<ICoProducer> = coproducers || [];
      const filtered = newCoproducers.filter((value: any, index: any) => {
        return index !== k;
      });
      onChange(filtered);

      if (onChangeRequiredFields) {
        onChangeRequiredFields();
      }
    },
    [coproducers, onChange, onChangeRequiredFields]
  );

  const handleChange = useCallback(
    (field: keyof ICoProducer, index: number, value: ICoProducer[keyof ICoProducer]) => {
      const newCops: Array<ICoProducer> = [...(coproducers || [])];
      newCops[index] = { ...newCops[index], [field]: value, edited: true };

      if (field === 'comission_value') {
        onChange(calculateNewCopsComission(newCops));
        return;
      }

      onChange(newCops);
    },
    [coproducers, onChange]
  );

  const onSearchSuccess = useCallback(
    (index: any, customer: any) => {
      const newCops: Array<ICoProducer> = [...(coproducers || [])];
      newCops[index] = { ...newCops[index], customer_id: customer.id, business_name: customer.business_name };
      onChange(newCops);
    },
    [coproducers, onChange]
  );

  const getActionsMessage = useCallback((pendingActionsVal: IrregularProduct['pending_actions']) => {
    const messages = [];

    if (pendingActionsVal.above_commission_limit) {
      messages.push(
        <div key='above_commission_limit' className='pb-2'>
          <Alert
            type='info'
            message='Verificamos que as comissões cadastradas neste produto ultrapassam o limite de 100%. Por gentileza, ajuste essa configuração até o dia 19/02.'
          />
        </div>
      );
    }

    if (pendingActionsVal.above_coproducers_limit) {
      messages.push(
        <div key='above_coproducers_limit' className='pb-2'>
          <Alert
            type='info'
            message='Verificamos que o número de coprodutores cadastrados em seu produto excede 14, que é a quantidade limite permitida em nossa plataforma. Por gentileza, normalize essa configuração até o dia 19/02'
          />
        </div>
      );
    }

    if (pendingActionsVal.commission_by_value) {
      messages.push(
        <div key='commission_by_value' className='pb-2'>
          <Alert
            type='info'
            message='Verificamos que este produto está configurado com comissão fixa por valor, a qual deixará de existir a partir do dia 19/02. Por gentileza, modifique para o modelo de comissão percentual.'
          />
        </div>
      );
    }

    return messages;
  }, []);

  const loadIrregularProduct = useCallback(async () => {
    const result = await productService.listIrregularProducts({ per_page: 20, current_page: 1 }, productId);
    const { data } = result;

    if (data.length) {
      setPendingActions(data[0].pending_actions);
    }
  }, [productId]);

  const handleValidateShouldAddCoproducer = useCallback(() => {
    if (shouldBlock) {
      setShowRegistryBlockModal(true);
      return;
    }

    add();
  }, [add, shouldBlock]);

  const handleCancelRegistryBlockModal = useCallback(() => {
    dismissBlock();
    setShowRegistryBlockModal(false);
    add();
  }, [add, dismissBlock]);

  useEffect(() => {
    loadIrregularProduct();
  }, [loadIrregularProduct]);

  useEffect(() => {
    const listCoproducers = coproducers && Array.isArray(coproducers) ? coproducers : [];

    if (!(window as any).sumCoproducersComissions) {
      const activeValue: number = parseFloat(
        listCoproducers
          .filter(c => (c.active || c.new) && !c.pending)
          .map(c => Number(c.comission_value.toString().replace(',', '.')))
          .reduce((v1, v2) => v1 + v2, 0)
          .toFixed(2)
      );
      (window as any).sumCoproducersComissions = activeValue;
    }
  }, [coproducers]);

  const listCoproducers = useMemo(() => {
    return coproducers && Array.isArray(coproducers) ? putIndexOnArray(coproducers) : [];
  }, [coproducers]);

  if (loading) {
    return (
      <div className='w-full text-center'>
        <Spin />
      </div>
    );
  }

  const listActive = listCoproducers.filter(c => c.active);
  const listNew = listCoproducers.filter(c => c.new && !c.pending);
  const listPending = listCoproducers.filter(c => c.pending);

  const activeValue: number = listCoproducers
    .filter(c => (c.active || c.new) && !c.pending)
    .filter(c => !c.is_producer)
    .map(c => Number(c.comission_value.toString().replace(',', '.')))
    .reduce((v1, v2) => v1 + v2, 0);

  const producerValue: number = MAX_COMISSION_TOTAL - activeValue;
  const hasActive = listActive.length > 0;
  const hasNew = listNew.length > 0;
  const hasPending = listPending.length > 0;

  const commissionBiggerThanExpected = activeValue > MAX_COMISSION_TOTAL;

  const hasCoproducers = hasActive || hasNew || hasPending;

  const firstComissionsAreDiffThanMax = (window as any).sumCoproducersComissions !== MAX_COMISSION_TOTAL;

  const listSavedCoproducers = listActive.map(cop => {
    const shouldOverrideProducerCommission = cop.is_producer && !firstComissionsAreDiffThanMax;
    const displayCop = shouldOverrideProducerCommission
      ? { ...cop, comission_value: commissionBiggerThanExpected ? 0 : producerValue }
      : cop;

    return (
      showActive && (
        <Col key={cop.originalIndex} span={24}>
          <PartnerCardPreview
            index={cop.originalIndex}
            coproducer={displayCop}
            disabled={shouldOverrideProducerCommission}
            onChangeField={handleChange}
            onChangeRequiredFields={onChangeRequiredFields}
            onRemove={() => remove(cop.originalIndex)}
            isProducer={cop.is_producer}
            onSearchSuccess={onSearchSuccess}
            coproducers={listCoproducers}
          />
        </Col>
      )
    );
  });

  const listNewCoproducers = listNew.map(cop => {
    return (
      showNew && (
        <Col key={cop.originalIndex} span={24}>
          {cop.customer_id === PROJECT_ELEVAR_ID ? (
            <SolidaritySquadForm
              index={cop.originalIndex}
              coproducer={cop}
              onRemove={() => remove(cop.originalIndex)}
              onChange={handleChange}
            />
          ) : (
            <CoProducerForm
              index={cop.originalIndex}
              coproducer={cop}
              onChangeField={handleChange}
              onChangeRequiredFields={onChangeRequiredFields}
              onRemove={() => remove(cop.originalIndex)}
              isProducer={false}
              onSearchSuccess={onSearchSuccess}
              coproducers={listCoproducers}
            />
          )}
        </Col>
      )
    );
  });

  const listPendingCoproducers = listPending.map(cop => {
    return (
      showPending && (
        <Col key={cop.originalIndex} span={24}>
          <PartnerCardPreview
            index={cop.originalIndex}
            coproducer={cop}
            onChangeField={handleChange}
            onChangeRequiredFields={onChangeRequiredFields}
            onRemove={() => remove(cop.originalIndex)}
            isProducer={true}
            onSearchSuccess={onSearchSuccess}
            disabled={true}
            pendingStatus={cop.status}
            coproducers={listCoproducers}
          />
        </Col>
      )
    );
  });

  return (
    <>
      <FormContent
        title='Parceiros do Produto'
        loading={loading}
        action={
          <Tooltip title={coProducerErrorMessage || undefined} placement='top'>
            <span className='inline-flex leading-none'>
              <Button
                icon={<Icon.UserPlus  />}
                color='default'
                variant='filled'
                className='m-1'
                onClick={handleValidateShouldAddCoproducer}
                disabled={!canAddCoproducer}
              >
                Adicionar novo parceiro
              </Button>
            </span>
          </Tooltip>
        }
      >
        <Row gutter={[16, 16]} align='top' justify='space-between'>
          {pendingActions && <Col span={24}>{getActionsMessage(pendingActions)}</Col>}
          {!hasElevarAdded && (
            <Col span={24}>
              <SolidaritySquadAlert onBtnClick={() => addElevar()} />
            </Col>
          )}
          <Col span={24}>
            <div className='flex flex-wrap items-center justify-between gap-2 rounded-sm border border-(--uizz-antd-color-border) p-3'>
              <strong>Mostrar:</strong>
              <div className='flex flex-wrap items-center gap-4'>
                <div className='flex items-center gap-2'>
                  <Switch checked={showActive} onChange={() => setShowActive(prev => !prev)} size='small' />
                  <Typography.Text className='text-base'>Ativos</Typography.Text>
                </div>
                <div className='flex items-center gap-2'>
                  <Switch checked={showNew} onChange={() => setShowNew(prev => !prev)} size='small' />
                  <Typography.Text className='text-base'>Novos</Typography.Text>
                </div>
                <div className='flex items-center gap-2'>
                  <Switch checked={showPending} onChange={() => setShowPending(prev => !prev)} size='small' />
                  <Typography.Text className='text-base'>Pendentes</Typography.Text>
                </div>
              </div>
            </div>
          </Col>
          {commissionBiggerThanExpected && (
            <Col span={24}>
              <Alert
                type='error'
                message={
                  <>
                    <Typography.Paragraph>
                      O valor total de comissão deve ser {MAX_COMISSION_TOTAL}% somando o valor de comissão de todos os
                      parceiros ativos.
                    </Typography.Paragraph>
                    <Typography.Paragraph>
                      Valor atual: <strong>{activeValue}%</strong>
                    </Typography.Paragraph>
                  </>
                }
              />
            </Col>
          )}

          {!hasCoproducers && (
            <div className='mx-4'>
              <Typography.Text className='text-base'>Não há parceiros cadastrados</Typography.Text>
            </div>
          )}
          {showNew && hasNew && (
            <>
              <Col span={24} ref={listNewRef}>
                <Typography.Title level={5} className='mb-0'>
                  Novos
                </Typography.Title>
              </Col>
              {listNewCoproducers}
            </>
          )}
          {showActive && hasActive && (
            <>
              {showNew && hasNew && (
                <Col span={24}>
                  <Divider className='my-2' />
                </Col>
              )}
              <Col span={24}>
                <Typography.Title level={5} className='mb-0'>
                  Ativos
                </Typography.Title>
              </Col>
              {listSavedCoproducers}
            </>
          )}
          {showPending && hasPending && (
            <>
              <Col span={24}>
                <Divider className='my-2' />
              </Col>
              <Col span={24}>
                <Typography.Title level={5} className='mb-0'>
                  Pendentes
                </Typography.Title>

                <Alert
                  className='mt-3'
                  type='info'
                  message={
                    <>
                      <Typography.Paragraph>
                        As alterações pendentes serão ativas apenas quando todos estiverem com o status:{' '}
                        <strong>APROVADO.</strong>
                      </Typography.Paragraph>
                      <Typography.Paragraph>
                        Será descartado todos os parceiros pendentes ao editar e salvar uma nova configuração de
                        parceiros.{' '}
                      </Typography.Paragraph>
                    </>
                  }
                  action={
                    <Button>
                      <a
                        href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807551969-Como-adicionar-um-Co-Produtor-%C3%93rbita-'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Saiba mais
                      </a>
                    </Button>
                  }
                />
              </Col>
              {listPendingCoproducers}
            </>
          )}
        </Row>
      </FormContent>

      <RegistryBlockModal
        opened={showRegistryBlockModal}
        isOwner={permissions?.owned}
        cancelable={registryCheck?.skip}
        onCancel={handleCancelRegistryBlockModal}
      />
    </>
  );
}

export default CoProducer;
